import { useCallback } from 'react';
import configService from '../../../../shared/services/configService';
import { getApiBaseUrl } from '../../../../core/config/api';
import type {
  Service,
  PersonnelConfig,
  VehiculeConfig,
  Competence,
  FonctionArmement,
} from '../../../../shared/types/config';
import type { BuildingTypeConfigItem } from '../components/config/tabs/BatimentsTab';
import type { PartenaireItem } from '../components/config/tabs/PartenairesTab';

interface CategorieSpeItem {
  id: number;
  nom: string;
  id_service: number;
  ordre: number;
  actif: number;
  service_nom?: string;
}

interface UseSystemConfigLoadOpts {
  activeTab: string;
  setLoading: (b: boolean) => void;
  setError: (e: string | null) => void;
  setPersonnelConfigs: (v: PersonnelConfig[]) => void;
  setVehiculeConfigs: (v: VehiculeConfig[]) => void;
  setServices: (v: Service[]) => void;
  setCompetences: (v: Competence[]) => void;
  setFonctionsArmement: (v: FonctionArmement[]) => void;
  setCategoriesMission: (v: { id: number; nom: string; created_at?: string }[]) => void;
  setCategoriesSpecialite: (v: CategorieSpeItem[]) => void;
  setBuildingTypesConfig: (v: BuildingTypeConfigItem[]) => void;
  setPartenairesConfig: (v: PartenaireItem[]) => void;
}

/** Hook extrait de SystemConfiguration.tsx : charge les données du panel
 *  Configuration en fonction de l'onglet actif. */
export function useSystemConfigLoad(opts: UseSystemConfigLoadOpts) {
  const {
    activeTab,
    setLoading,
    setError,
    setPersonnelConfigs,
    setVehiculeConfigs,
    setServices,
    setCompetences,
    setFonctionsArmement,
    setCategoriesMission,
    setCategoriesSpecialite,
    setBuildingTypesConfig,
    setPartenairesConfig,
  } = opts;

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'personnel') {
        const [personnelRes, servicesRes, competencesRes] = await Promise.all([
          configService.getPersonnelConfigs(),
          configService.getServices(),
          configService.getCompetences(),
        ]);
        if (personnelRes.success && personnelRes.data) {
          setPersonnelConfigs(personnelRes.data);
        } else {
          setError(personnelRes.error || 'Erreur lors du chargement des personnels');
        }
        if (servicesRes.success && servicesRes.data) {
          setServices(servicesRes.data);
        }
        if (competencesRes.success && competencesRes.data) {
          setCompetences(competencesRes.data);
        }
      } else if (activeTab === 'vehicules') {
        const [vehiculeRes, servicesRes, personnelRes, competencesRes, fonctionsRes] = await Promise.all([
          configService.getVehiculeConfigs(),
          configService.getServices(),
          configService.getPersonnelConfigs(),
          configService.getCompetences(),
          configService.getFonctionsArmement(),
        ]);
        if (vehiculeRes.success && vehiculeRes.data) {
          setVehiculeConfigs(vehiculeRes.data);
        } else {
          setError(vehiculeRes.error || 'Erreur lors du chargement des véhicules');
        }
        if (servicesRes.success && servicesRes.data) {
          setServices(servicesRes.data);
        }
        if (personnelRes.success && personnelRes.data) {
          setPersonnelConfigs(personnelRes.data);
        }
        if (competencesRes.success && competencesRes.data) {
          setCompetences(competencesRes.data);
        }
        if (fonctionsRes.success && fonctionsRes.data) {
          setFonctionsArmement(fonctionsRes.data);
        }
      } else if (activeTab === 'competences') {
        const [competencesRes, servicesRes] = await Promise.all([
          configService.getCompetences(),
          configService.getServices(),
        ]);
        if (competencesRes.success && competencesRes.data) {
          setCompetences(competencesRes.data);
        } else {
          setError(competencesRes.error || 'Erreur lors du chargement des compétences');
        }
        if (servicesRes.success && servicesRes.data) {
          setServices(servicesRes.data);
        }
      } else if (activeTab === 'fonctions-armement') {
        const [fonctionsRes, competencesRes] = await Promise.all([
          configService.getFonctionsArmement(),
          configService.getCompetences(),
        ]);
        if (fonctionsRes.success && fonctionsRes.data) {
          setFonctionsArmement(fonctionsRes.data);
        } else {
          setError(fonctionsRes.error || 'Erreur lors du chargement des fonctions d\'armement');
        }
        if (competencesRes.success && competencesRes.data) {
          setCompetences(competencesRes.data);
        }
      } else if (activeTab === 'batiments') {
        const token = localStorage.getItem('token');
        const apiUrl = getApiBaseUrl();
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${apiUrl}/api/buildings/config`, { headers, credentials: 'include' });
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) setBuildingTypesConfig(data.data);
      } else if (activeTab === 'partenaires') {
        const token = localStorage.getItem('token');
        const apiUrl = getApiBaseUrl();
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${apiUrl}/api/config/partenaires`, { headers, credentials: 'include' });
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) setPartenairesConfig(data.data);
      } else if (activeTab === 'categories-mission' || activeTab === 'categories-spe') {
        const token = localStorage.getItem('token');
        const apiUrl = getApiBaseUrl();
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
        if (activeTab === 'categories-mission') {
          const res = await fetch(`${apiUrl}/api/cta/categories-mission`, { headers, credentials: 'include' });
          const data = await res.json();
          if (data.success && Array.isArray(data.data)) setCategoriesMission(data.data);
        } else {
          const [catSpeRes, servicesRes] = await Promise.all([
            configService.getCategoriesSpecialite(),
            configService.getServices(),
          ]);
          if (catSpeRes.success && Array.isArray(catSpeRes.data)) setCategoriesSpecialite(catSpeRes.data);
          if (servicesRes.success && servicesRes.data) setServices(servicesRes.data);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  return loadAllData;
}
